import { useEffect } from "react"
import { Link } from "react-router-dom"
import AssetPath from "../../AssetPath/AssetPath"
import { companyName, productName } from "../../config"
import "./Landing.scss"

const contactEmail =
  process.env.REACT_APP_CONTACT_US_EMAIL || "support@xpanstech.com"
const updated = "24 October 2026"

const privacySections = [
  {
    id: "who",
    title: "Who operates Xpanse",
    body: [
      `${productName} is a product of ${companyName}. The console at xpanse.xpanstech.com and the API at xpanse-api.xpanstech.com are the service this policy describes.`,
      `Questions about this policy go to ${contactEmail}.`,
    ],
  },
  {
    id: "what",
    title: "What the service is for",
    body: [
      `${productName} is endpoint management for more than one company. An organization keeps its own users, devices, policies, and operational records. A person signs in with that organization’s slug and sees that fleet.`,
    ],
  },
  {
    id: "collect",
    title: "Information we collect",
    body: [
      "Account details: name, work email, password, job title, phone number, and the role you choose when you start a trial or accept an invitation. Passwords are stored as a hash. Two-factor authentication stores a secret for that account when you turn it on.",
      "Organization details: company name, organization slug, company size, the kind of team you said you are, plan, trial dates, and the administrator’s contact email.",
      "Device details: hostname, operating system, check-in time, installed software, update and vulnerability status, policy results, and the organization that enrolled the agent. If an administrator turns on activity screenshots or profile images, those files are stored for that organization.",
      "Operational records: sign-in events, actions taken in the console, mail we send about the account, and support messages you send us.",
    ],
  },
  {
    id: "use",
    title: "How we use it",
    body: [
      `We use this information to run ${productName}: to create the organization, sign people in, enroll endpoints, apply the policies that organization’s administrators choose, send setup and trial mail, and keep the service reliable.`,
      "We do not sell customer or device information. We do not use one organization’s fleet to advertise to another.",
    ],
  },
  {
    id: "separation",
    title: "Organizations stay separate",
    body: [
      "Device inventory, users, and policies are stored against the organization that enrolled them. People in one organization do not get another organization’s console by signing in.",
      `A platform administrator at ${companyName} can invite an organization, see that it exists, and suspend it. That access is for operating the service, not for mixing customer fleets.`,
    ],
  },
  {
    id: "trial",
    title: "The 30-day trial",
    body: [
      "Starting a trial creates the company and the first administrator immediately. We ask for the details on the signup form so the console can open. No card number is collected for the trial.",
      "When the trial ends, sign-in for that organization stops until the organization is continued. The records are not deleted on the day the trial ends.",
    ],
  },
  {
    id: "share",
    title: "Who else receives information",
    body: [
      `Mail about invites, trials, and account security is sent through the mailbox ${contactEmail} on Microsoft 365. Device files an administrator uploads, such as screenshots, are stored in object storage used by the service.`,
      "We share information if the law requires it, or if an organization administrator asks us to support that organization’s own console.",
    ],
  },
  {
    id: "keep",
    title: "How long we keep it",
    body: [
      "Account and device records stay while the organization is active, invited, or in trial, and for as long as needed to operate, secure, or restore the service after access ends.",
      "You can ask us to remove an organization that you administer. We will remove or disconnect the account and device records we hold for that organization, except where we must keep a record for security, billing, or a legal duty.",
    ],
  },
  {
    id: "security",
    title: "Security",
    body: [
      "The console is served over HTTPS. Sign-in can require a second factor. Organization administrators decide who else may use their console and which devices to enroll.",
      "No online service can promise that information will never be accessed improperly. Tell us at the address above if you believe an account or device record has been exposed.",
    ],
  },
  {
    id: "choices",
    title: "Your choices",
    body: [
      "You can update your account from the console, turn on two-factor authentication, and stop using a device by unenrolling it. Organization administrators control invitations and suspension for their company.",
      `To ask for a copy of your account information, or to ask for deletion, write to ${contactEmail} from the work email on the account and include the organization slug.`,
    ],
  },
]

const termsSections = [
  {
    id: "agreement",
    title: "The agreement",
    body: [
      `These terms cover use of ${productName}, operated by ${companyName}. By creating a trial, accepting an invitation, or signing in, you agree to these terms and to the privacy policy.`,
      `If you use ${productName} for a company, you confirm that you are allowed to bind that company to these terms.`,
    ],
  },
  {
    id: "service",
    title: "The service",
    body: [
      `${productName} provides a console and agents for inventory, patching, software deployment, policies, and related operations for the organization you sign in to.`,
      "Features can change as the product is updated. A change that removes a function you rely on during a paid term, once a paid term exists, will be described before it takes effect.",
    ],
  },
  {
    id: "trial-terms",
    title: "Trials",
    body: [
      "A new company can start a 30-day trial without a credit card. The trial includes the console for that organization and the right to enroll its endpoints during those 30 days.",
      "At the end of the trial, sign-in stops unless the organization is continued. Starting a trial does not by itself create a paid subscription.",
      `${companyName} may refuse or end a trial that is used to abuse the service, to access another organization, or to register a company with details that are not real.`,
    ],
  },
  {
    id: "accounts",
    title: "Accounts and organizations",
    body: [
      "Each organization has a slug. You sign in with that slug, your email, and your password. The same email may exist in a different organization as a separate account.",
      "You are responsible for keeping the password and the second factor private, and for the people you invite into the organization.",
      "A platform administrator may invite a company, suspend it, or continue it. Suspending an organization blocks sign-in and leaves its data in place.",
    ],
  },
  {
    id: "devices",
    title: "Devices you enroll",
    body: [
      "Enroll only endpoints the organization is allowed to manage. The install command carries the organization, and the agent then reports into that tenant.",
      "Organization administrators are responsible for the policies, updates, restarts, and software they apply to those devices.",
    ],
  },
  {
    id: "use-limits",
    title: "Acceptable use",
    body: [
      `Do not use ${productName} to break into systems you do not administer, to copy another organization’s data, to disrupt the service, or to send unsolicited mail through it.`,
      "Do not probe, scan, or overload the API except as needed to operate your own enrolled devices. Do not share sign-in details outside the organization they belong to.",
    ],
  },
  {
    id: "data",
    title: "Your data",
    body: [
      "The organization keeps the rights to the device and account information it submits. You grant us the right to host, process, and display that information only so we can provide the service to that organization.",
      "The privacy policy describes what is collected and how organizations are kept apart.",
    ],
  },
  {
    id: "availability",
    title: "Availability and responsibility",
    body: [
      `${productName} is provided as an operational console. Maintenance, a failed endpoint, or a network fault can interrupt check-in or a scheduled job. We work to restore the service, and we do not promise uninterrupted access.`,
      `To the extent the law allows, ${companyName} is not liable for lost data, lost profits, or damage on an enrolled device caused by a policy, update, or restart that an administrator of that organization chose to run.`,
    ],
  },
  {
    id: "end",
    title: "Ending access",
    body: [
      "You may stop using the console at any time. An organization administrator may ask us to close the organization.",
      `We may suspend access that breaks these terms. These terms are governed by the laws of India. Write to ${contactEmail} before starting a formal dispute so we can try to resolve it.`,
    ],
  },
]

const LegalPage = ({ kind }) => {
  const privacy = kind === "privacy"
  const sections = privacy ? privacySections : termsSections
  const title = privacy ? "Privacy policy" : "Terms of use"

  useEffect(() => {
    const previous = document.title
    document.title = `${title} · ${productName}`
    return () => {
      document.title = previous
    }
  }, [title])

  return (
    <div className="landing">
      <header className="landing__bar">
        <div className="landing__nav">
          <Link to="/" className="landing__brand">
            <img src={AssetPath.brand.appIconCircle} alt="" />
            <span>
              <strong>{productName}</strong>
              <small>{companyName}</small>
            </span>
          </Link>
          <nav className="landing__nav-links">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/login">Sign in</Link>
            <Link to="/signup" className="landing__nav-cta">
              Start free trial
            </Link>
          </nav>
        </div>
      </header>

      <main className="landing__legal">
        <p className="landing__eyebrow">{privacy ? "Privacy" : "Terms"}</p>
        <h1>{title}</h1>
        <p className="landing__section-copy">
          {privacy
            ? `How ${companyName} handles account, organization, and device information in ${productName}.`
            : `The rules for using ${productName}, including the 30-day trial.`}
        </p>
        <p className="landing__legal-meta">Updated {updated}</p>

        <nav className="landing__legal-toc" aria-label="On this page">
          {sections.map((section) => (
            <a key={section.id} href={`#${section.id}`}>
              {section.title}
            </a>
          ))}
        </nav>

        {sections.map((section) => (
          <section key={section.id} id={section.id}>
            <h2>{section.title}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}

        <p className="landing__legal-contact">
          {companyName}
          {" · "}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
        </p>
        <p>
          <Link to="/">Back to {productName}</Link>
          {" · "}
          <Link to={privacy ? "/terms" : "/privacy"}>
            {privacy ? "Terms of use" : "Privacy policy"}
          </Link>
        </p>
      </main>
    </div>
  )
}

export default LegalPage
